import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MovieCard from '@/components/base/MovieCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { getMoviesByCategory } from '@/mocks/movies';
import { getCategoryBySlug, categories } from '@/mocks/categories';
import NotFound from '@/pages/NotFound';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = getCategoryBySlug(slug || '');
  const categoryMovies = slug ? getMoviesByCategory(slug) : [];
  const [activeCategory, setActiveCategory] = useState(slug || '');

  if (!category) {
    return <NotFound />;
  }

  const pageTitle = `${category.name} - 日韩大片在线观看`;
  const pageDescription = `${category.description}最新${category.name}推荐，高清免费在线播放，每日更新日韩大片资源。`;

  const webpageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: pageTitle,
    description: pageDescription,
    url: `https://rihan-dapian.com/category/${slug}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: categoryMovies.map((movie, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Movie',
          name: movie.title,
          url: `https://rihan-dapian.com/article/${movie.id}`,
          image: movie.coverImage,
          dateCreated: movie.releaseDate,
          director: { '@type': 'Person', name: movie.director },
        },
      })),
    },
  };

  const handleCategoryChange = (newSlug: string) => {
    setActiveCategory(newSlug);
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(webpageSchema)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Page Header */}
          <section className="bg-background-100 py-10 md:py-14">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: category.name },
                ]}
              />
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mt-4 mb-3">
                {category.name}
              </h1>
              <p className="text-foreground-600 max-w-2xl">
                {category.description}
              </p>
            </div>
          </section>

          {/* Category Tabs */}
          <section className="border-b border-background-200/70 bg-background-50 sticky top-16 z-40">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex items-center gap-1 overflow-x-auto py-3 -mx-1 px-1">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    onClick={() => handleCategoryChange(cat.slug)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      activeCategory === cat.slug || slug === cat.slug
                        ? 'bg-primary-500 text-white'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Movie Grid */}
          <section className="py-10 md:py-14">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              {categoryMovies.length > 0 ? (
                <>
                  <div className="flex items-center justify-between mb-6">
                    <p className="text-sm text-foreground-500">
                      共找到 <strong className="text-foreground-900">{categoryMovies.length}</strong> 部{category.name}
                    </p>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5" data-product-shop>
                    {categoryMovies.map((movie) => (
                      <MovieCard key={movie.id} movie={movie} />
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center py-20">
                  <span className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-2xl bg-background-100">
                    <i className="ri-movie-line text-foreground-300 text-3xl"></i>
                  </span>
                  <h3 className="font-heading text-lg font-semibold text-foreground-700 mb-2">暂无影片</h3>
                  <p className="text-sm text-foreground-500">该分类下暂无电影资源，请浏览其他分类。</p>
                </div>
              )}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}